import axios from "axios";
import type { RumEvent } from "@datadog/browser-rum";

// Error Tracking fingerprints browser errors from the stack trace, and every
// failed axios request shares the same one (createError -> settle -> xhr), so
// without this the whole app's 4xx/5xx collapse into a single issue. The RUM
// SDK reads dd_fingerprint off any Error it collects, which covers both the
// unhandled rejection and the console.error copy of the same failure.
const UUID_SEGMENT = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi;
const NUMERIC_SEGMENT = /\/\d+(?=\/|$)/g;

type AxiosLikeError = {
  config?: { url?: string; method?: string };
  response?: { status?: number };
};

const axiosErrorFingerprint = (error: AxiosLikeError, origin: string): string => {
  const method = (error.config?.method || "get").toUpperCase();
  const status = error.response?.status ?? "no-response";
  const url = error.config?.url;
  let path = "unknown";
  if (url) {
    try {
      path = new URL(url, origin).pathname
        .replace(UUID_SEGMENT, "/{id}")
        .replace(NUMERIC_SEGMENT, "/{id}");
    } catch {
      // Unparseable url -- group those together rather than drop them.
    }
  }
  return `axios ${method} ${path} ${status}`;
};

export const initDatadogErrorGrouping = () => {
  axios.interceptors.response.use(
    (response) => response,
    (error: any) => {
      if (error instanceof Error) {
        (error as Error & { dd_fingerprint?: string }).dd_fingerprint =
          axiosErrorFingerprint(error, window.location.origin);
      }
      return Promise.reject(error);
    }
  );
};

// A dropped connection fails every request in flight, and each failure reaches
// RUM twice (the unhandled rejection and the console.error copy), so one user's
// bad wifi manufactures Error Tracking issues that no code change can fix.
// Discard them -- the status-0 resource events are untouched and are the better
// signal anyway: one per request, and they carry the url.
// When Chrome's own network stack times out, the XHR fires "timeout" even with
// no timeout set, so axios reports "timeout of 0ms exceeded" for the same
// dropped connection. A configured timeout is a different signal and is kept.
// Chrome also reports "ResizeObserver loop completed with undelivered
// notifications" when a resize callback changes layout; it delivers the rest
// next frame, so the error is harmless and has no stack. Chrome sends
// "Ignored attempt to cancel a touchstart event" as an intervention report when
// react-draggable calls preventDefault on a touch that is already scrolling.
// Firefox rejects pending browser promises with "the browsing context is going
// away" when the tab unloads. Howler calls AudioContext.resume() on each click
// with no catch, so a click that navigates away leaves this rejection behind.
// iOS WebKit rejects the same call with "Failed to start the audio device" when
// the tap comes before the page is active, e.g. just after a background reload.
// Browsers replace an error from a cross-origin script loaded without a
// crossorigin attribute (the HubSpot loader and the pixels it injects) with a
// bare "Script error." that has no stack. Our own bundles are same-origin, so
// their errors always keep a stack and never take this form.
const IGNORED_CLIENT_ERRORS = [
  "Network Error",
  "Request aborted",
  "timeout of 0ms exceeded",
  "ResizeObserver loop",
  "Ignored attempt to cancel a touch",
  "browsing context is going away",
  "Failed to start the audio device",
  "Script error.",
];

// HubSpot's ad pixel (js-na2.hsadspixel.net/pixels.js) parses its config reply
// as JSON. A network filter that blocks trackers answers with a plain-text page
// ("Restricted"), so the parse throws a SyntaxError that no change in our code
// can fix. Match the script host in the stack, not the message: a message match
// would also drop a JSON.parse error from our own code.
// HubSpot's chat and popup widget (js-<region>.hubspot.com/web-interactives-embed.js)
// logs "Failed to open IndexedDB" with console.error when the browser refuses
// storage, for example in Incognito or with site data blocked. The match leaves
// out the regional host, so js-na1, js-na2 and js-eu1 are all dropped.
const IGNORED_ERROR_SOURCES = ["hsadspixel.net", "hubspot.com/web-interactives-embed.js"];

export const dropIgnoredClientErrors = (event: RumEvent): boolean =>
  !(
    event.type === "error" &&
    (IGNORED_CLIENT_ERRORS.some((m) => event.error.message.includes(m)) ||
      IGNORED_ERROR_SOURCES.some((s) => event.error.stack?.includes(s)))
  );
